/**
 * RetryButton — the manual exit from a stalled or failed data state.
 *
 * Lives here rather than in one route because both search pages need the same
 * affordance and, while it was module-private to `SearchDetailPage`, its sibling
 * hand-rolled a second copy: same tokens, same disabled treatment, same label
 * pair, and only one of the two got the hover fix below.
 *
 * `hover:border-*` rather than a background change: the warning banners this
 * usually sits in already carry `bg-warning-muted`, so a `hover:bg-warning-muted`
 * is a visual no-op — the button looks interactive and answers a hover with
 * nothing.
 *
 * Design-system token utilities only; no inline `style{{}}`.
 */

export interface RetryButtonProps {
  onClick: () => void;
  /** Disables the button and swaps the label while the retry is in flight. */
  busy: boolean;
  label?: string;
  /** Shown in place of `label` while `busy`. */
  busyLabel?: string;
  /** Matches the banner it sits in. `accent` is the in-progress notice; `error`
   *  the full-page failure; `warning` (default) the soft banners. */
  tone?: "warning" | "accent" | "error";
}

const TONE_CLASSES = {
  warning: "border-warning-strong text-warning-ink hover:border-warning-ink",
  accent: "border-border text-text hover:border-border-hover",
  error: "border-error-strong text-error hover:border-error",
} as const;

export function RetryButton({
  onClick,
  busy,
  label = "Try again",
  busyLabel = "Checking…",
  tone = "warning",
}: RetryButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      className={`rounded-md border px-2 py-1 text-label disabled:opacity-50 ${TONE_CLASSES[tone]}`}
    >
      {busy ? busyLabel : label}
    </button>
  );
}
